import { useThemeMode, useThemePreference } from "@meiden-connect/ui";
import {
  AppHeader,
  AppLogo,
  AppThemeToggle,
  AppUserChip,
  MEIDEN_CONNECT_MARK_SRC,
  MeidenConnectPoweredByLogo,
} from "@osakabe/auth-service-provider/shell";
import { useAuthSession } from "../../auth/auth-session-context";
import { useCurrentActor } from "../../auth/use-current-actor";
import { getWebConfig } from "../../env";
import { saveDashboardThemePreference } from "../../theme/theme-preference";
import { UserAvatar } from "./user-avatar";

// ヘッダーの枠・ロゴ・Powered by ロゴ・テーマ切替・ユーザーチップは共通シェル
// (@osakabe/auth-service-provider/shell) に委譲する。データ取得 (認証・テーマ・アバター) は
// アプリ側に残し、既存のクラス名 (dashboard-header 系) を className 注入して見た目を維持する。
export function DashboardHeader(): JSX.Element {
  const themeMode = useThemeMode();
  const [, setThemePreference] = useThemePreference();
  const isLoggedIn = getWebConfig().authMode === "required";

  // AppThemeToggle は日本語ラベルを "dark" / "light" に正規化して渡してくるため、
  // 永続化処理だけをここで行う。
  const handleChangeThemeMode = (next: "dark" | "light"): void => {
    setThemePreference(next);
    saveDashboardThemePreference(next);
  };

  return (
    <AppHeader
      brand={
        <AppLogo
          appName="リファインメントキャンバス"
          classNames={{
            root: "dashboard-logo",
            mark: "dashboard-logo-mark",
            version: "dashboard-logo-version",
          }}
          logoSrc="/app-icon.png"
          version={__APP_VERSION__}
        />
      }
      classNames={{ root: "dashboard-header", actions: "dashboard-header-actions" }}
      actions={
        <>
          <MeidenConnectPoweredByLogo
            ariaLabel="MEIDEN CONNECT ソリューションカタログ"
            imageAlt="MEIDEN CONNECT"
            classNames={{
              root: "meiden-connect-logo",
              disabled: "meiden-connect-logo-disabled",
              powered: "meiden-connect-logo-powered",
              image: "meiden-connect-logo-img",
            }}
            darkLogoSrc="/meiden-connect-logo-dark.png"
            disabledTitle="ログイン中のみ利用できます"
            enabledTitle="ソリューションカタログを開く"
            isLoggedIn={isLoggedIn}
            lightLogoSrc="/meiden-connect-logo-light.png"
            markSrc={MEIDEN_CONNECT_MARK_SRC}
            themeMode={themeMode}
          />
          <AppThemeToggle
            classNames={{ switch: "theme-toggle-switch" }}
            onChangeThemeMode={handleChangeThemeMode}
            themeMode={themeMode}
          />
          {isLoggedIn ? <RequiredUserChip /> : <MockUserChip />}
        </>
      }
    />
  );
}

function RequiredUserChip(): JSX.Element {
  // SDK Provider が main tree から外れたため、ユーザー情報・サインアウト導線とも
  // AuthSessionContext (SessionSnapshot) を経由して取得する。
  const { user, signOut } = useAuthSession();
  const actor = useCurrentActor();
  const authServiceOrigin = getWebConfig().authServiceOrigin;
  const profileUrl =
    authServiceOrigin && user !== undefined
      ? `${authServiceOrigin}/users/${user.userId}/profile`
      : undefined;

  return (
    <AppUserChip
      classNames={{
        root: "dashboard-user-chip",
        identityLink: "dashboard-user-identity-link",
        meta: "dashboard-user-meta",
        logoutButton: "dashboard-header-icon-button",
      }}
      name={actor.displayName}
      onSignOut={() => void signOut()}
      profileLinkTitle="ユーザー情報を開く"
      profileUrl={profileUrl}
      renderAvatar={() => (
        <UserAvatar
          alt="ログインユーザーのアイコン"
          displayName={actor.displayName}
          src={actor.avatarImageUrl}
        />
      )}
      subLabel={user?.email ?? ""}
    />
  );
}

function MockUserChip(): JSX.Element {
  const actor = useCurrentActor();
  // ローカル確認用の mock 表示にはログアウト導線が無いため onSignOut を渡さない
  // (未指定でログアウト ボタンを描画しない / @osakabe/auth-service-provider 0.2.1 以降)。
  return (
    <AppUserChip
      ariaLabel={`ローカル確認ユーザー: ${actor.displayName}`}
      classNames={{ root: "dashboard-user-chip", meta: "dashboard-user-meta" }}
      name={actor.displayName}
      renderAvatar={() => (
        <UserAvatar
          alt="ローカル確認ユーザーのアイコン"
          displayName={actor.displayName}
          src={actor.avatarImageUrl}
        />
      )}
      subLabel="ローカル確認"
    />
  );
}
